import {
  ALL,
  CLEAR_FILTERS,
  RESEARCH_DOMAINS,
  NO_PROJECTS_FOUND,
  RESEARCH_ACTIVITIES,
  RESOURCE_TYPES,
  translate,
} from "shared/utils/translate";
import { Button } from "shared/components/Button";
import { CardsContainer } from "shared/components/CardsContainer";
import { CategoriesBar } from "shared/components/CategoriesBar";
import { MaxWidth } from "shared/components/MaxWidth";
import { OverviewIntro } from "shared/components/OverviewIntro";
import { Page } from "clariah/components/Page";
import { Pagination } from "shared/components/Pagination";
import {
  POSTS_PER_PAGE,
  PAGE_TYPE,
  PROJECT_OVERVIEW_TYPE,
} from "clariah/utils/constants";
import {
  TINY,
  SMALL,
  LARGE,
  TYPE_RICHTEXT_SIMPLE,
  TYPE_LINK,
} from "shared/utils/constants";
import { ProjectCategoryIntro } from "shared/components/ProjectCategoryIntro";
import { RichText } from "shared/components/RichText";
import { SelectFilters } from "shared/components/SelectFilters";
import { Spacing } from "shared/components/Spacing";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";

export const ProjectOverviewPage = ({
  footer,
  intro,
  mirrorPath,
  navigation,
  posts,
  seo,
  title,
}) => {
  const filterValuesMounted = useRef(false);

  const [filterValues, setFilterValues] = useState({});
  const [properties, setProperties] = useState(getPropertiesForPosts(posts));

  const { categories, researchDomains, researchActivities, resourceTypes } =
    properties;

  const router = useRouter();

  useEffect(() => {
    setProperties(getPropertiesForPosts(posts));
  }, [posts]);

  useEffect(() => {
    const { categories, page, ...rest } = router.query;

    if (JSON.stringify(rest) !== JSON.stringify(filterValues)) {
      setFilterValues(rest);
    }
  }, [router.query]);

  useEffect(() => {
    // To prevent initial router hitting with empty values.
    if (!filterValuesMounted.current) {
      filterValuesMounted.current = true;
      return;
    }

    const { categories, page, ...rest } = router.query;
    let query = { page: 1 };

    if (categories) {
      query = { ...query, categories };
    }

    if (JSON.stringify(rest) !== JSON.stringify(filterValues)) {
      query = { ...query, ...filterValues };
    } else {
      query = { ...query, ...rest };
    }

    if (JSON.stringify(router.query) !== JSON.stringify(query)) {
      router.push({
        pathname: router.pathname,
        query,
      });
    }
  }, [filterValues]);

  const filteredPosts = getFilteredPosts(posts, router.query);

  const page = +router.query.page || 1;
  const postStart =
    !router.query.categories && page > 1
      ? POSTS_PER_PAGE * (page - 1) + 1
      : POSTS_PER_PAGE * (page - 1);
  const postEnd =
    !router.query.categories && page === 1
      ? postStart + POSTS_PER_PAGE + 1
      : postStart + POSTS_PER_PAGE;

  const filteredPostsForPage = filteredPosts.slice(postStart, postEnd);

  const activeCategory = (categories || []).find(
    ({ slug }) => router.query.categories === slug
  );

  return (
    <Page
      description={seo?.description || intro}
      footer={footer}
      hasNewsletter
      image={seo?.image}
      mirrorPath={mirrorPath}
      navigation={navigation}
      title={seo?.title || title}
    >
      <MaxWidth size={LARGE}>
        <Spacing size={SMALL}>
          <OverviewIntro description={intro} title={title} />
        </Spacing>
        <CategoriesBar
          items={[
            {
              isActive: !router.query.categories,
              label: translate(ALL, router.locale),
              href: {
                pathname: `/${router.locale}${router.pathname}`,
              },
              type: PAGE_TYPE,
            },
            ...(categories || []).map(({ name, slug }) => ({
              isActive: router.query.categories === slug,
              label: name,
              href: {
                pathname: `/${router.locale}${router.pathname}`,
                query: { categories: slug },
              },
              type: PAGE_TYPE,
            })),
          ]}
        />
        <SelectFilters
          filters={[
            {
              label: translate(RESEARCH_DOMAINS, router.locale),
              value: "researchDomains",
              options: (researchDomains || []).map(({ title, slug }) => ({
                label: title,
                value: slug,
              })),
            },
            {
              label: translate(RESOURCE_TYPES, router.locale),
              value: "resourceTypes",
              options: (resourceTypes || []).map(({ title, slug }) => ({
                label: title,
                value: slug,
              })),
            },
            {
              label: translate(RESEARCH_ACTIVITIES, router.locale),
              value: "researchActivities",
              options: (researchActivities || []).map(({ title, slug }) => ({
                label: title,
                value: slug,
              })),
            },
          ]}
          values={filterValues}
          setValues={setFilterValues}
        />
        {activeCategory?.intro && (
          <Spacing size={TINY}>
            <ProjectCategoryIntro
              title={activeCategory.name}
              description={activeCategory.intro}
            />
          </Spacing>
        )}
        <Spacing size={TINY}>
          {filteredPostsForPage.length ? (
            <CardsContainer
              items={filteredPostsForPage.map((item, index) => ({
                ...item,
                featured:
                  !router.query.categories &&
                  (!router.query.page || +router.query.page === 1) &&
                  index === 0,
              }))}
            />
          ) : (
            <>
              <RichText type={TYPE_RICHTEXT_SIMPLE}>
                <p>{translate(NO_PROJECTS_FOUND, router.locale)}</p>
              </RichText>
              <Button
                href="/"
                hrefType={PROJECT_OVERVIEW_TYPE}
                type={TYPE_LINK}
              >
                {translate(CLEAR_FILTERS, router.locale)}
              </Button>
            </>
          )}
        </Spacing>
        {filteredPosts.length > POSTS_PER_PAGE && (
          <Pagination
            current={+router.query.page || 1}
            total={Math.ceil(filteredPosts.length / POSTS_PER_PAGE)}
          />
        )}
      </MaxWidth>
    </Page>
  );
};

function getFilteredPosts(posts, { page, ...rest }) {
  return [...posts].filter((post) => {
    const matches = Object.entries(rest || {})
      .map(([key, value]) => {
        if (value === "") {
          return true;
        }

        if (!post[key] || post[key].length < 1) {
          return false;
        }

        const keyMatch =
          post[key]
            ?.filter((item) => item !== null)
            .map(({ slug }) => slug.current)
            .indexOf(value) > -1;

        return keyMatch;
      })
      .filter(Boolean);

    return matches.length === Object.keys(rest).length;
  });
}

function getPropertiesForPosts(posts) {
  const categories = getUniqueNameAndSlug([...posts], "categories");
  const researchDomains = getUniqueNameAndSlug([...posts], "researchDomains");
  const researchActivities = getUniqueNameAndSlug(
    [...posts],
    "researchActivities"
  );
  const resourceTypes = getUniqueNameAndSlug([...posts], "resourceTypes");

  return { categories, researchDomains, researchActivities, resourceTypes };
}

function getUniqueNameAndSlug(posts, key) {
  return posts.reduce(
    (acc, post) =>
      [
        ...acc,
        ...(post[key] || [])
          .filter((item) => item !== null)
          .map((item) => ({
            intro: item?.intro,
            title: item?.title,
            name: item?.name,
            slug: item?.slug.current,
          }))
          .filter(
            ({ slug }) => acc.findIndex((item) => item.slug === slug) === -1
          ),
      ].sort((a, b) =>
        a.name ? (a.name < b.name ? -1 : 1) : a.title < b.title ? -1 : 1
      ),
    []
  );
}
